import type { CodeEditorLanguage } from "@v1/components/modals/CodeEditorModal";

import * as React from "react";

import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectDraftComponentType,
  selectPropCSSContent,
  selectPropHTMLContent,
  selectPropLiquidContent,
} from "@v1/reducers/core-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { useOpenModal } from "@v2/store/modalStore";
import { getRenderData } from "replo-runtime/store/components";

const useOpenCodeEditor = () => {
  const applyComponentAction = useApplyComponentAction();
  const openModal = useOpenModal();
  const store = useEditorStore();

  const supportedShortcutTypes = new Set([
    "htmlCodeEditor",
    "cssCodeEditor",
    "liquidCodeEditor",
    "rawHtmlContent",
  ]);

  const draftComponentType = useEditorSelector(selectDraftComponentType);
  const openCodeEditor = React.useCallback(() => {
    const htmlContent = selectPropHTMLContent(store.getState());
    const cssContent = selectPropCSSContent(store.getState());
    const liquidContent = selectPropLiquidContent(store.getState());

    const componentType = draftComponentType as
      | "htmlCodeEditor"
      | "cssCodeEditor"
      | "liquidCodeEditor"
      | "rawHtmlContent"
      | "shopifyRawLiquid";

    const editorConfig = {
      htmlCodeEditor: {
        value: htmlContent,
        language: "html",
        propName: "_htmlContent",
      },
      cssCodeEditor: {
        value: cssContent,
        language: "css",
        propName: "_css",
      },
      liquidCodeEditor: {
        value: liquidContent,
        language: "liquid",
        propName: "_liquidContent",
      },
      rawHtmlContent: {
        value: htmlContent,
        language: "html",
        propName: "_htmlContent",
      },
      shopifyRawLiquid: {
        value: liquidContent,
        language: "liquid",
        propName: "_liquidContent",
      },
    }[componentType];

    openModal({
      type: "codeEditorModal",
      props: {
        value: editorConfig.value,
        language: editorConfig.language as CodeEditorLanguage,
        onChange: (value: string) => {
          return applyComponentAction({
            type: "setProps",
            value: { [editorConfig.propName]: value },
          });
        },
      },
    });
  }, [store, openModal, applyComponentAction, draftComponentType]);

  if (!draftComponentType) {
    return;
  }

  const renderData = getRenderData(draftComponentType);
  const currentTypeRenderData = renderData?.customProps?.find((customProp) => {
    return supportedShortcutTypes.has(customProp.type);
  });

  if (!currentTypeRenderData) {
    return;
  }

  return openCodeEditor;
};

export default useOpenCodeEditor;
